// CIE LCH ↔ sRGB hex, following the CSS Color 4 conversion chain:
// LCH → Lab → XYZ(D50) → [Bradford] → XYZ(D65) → linear sRGB → gamma → hex.
// Used by the custom color picker, which drives a single LCH hue with L and C
// fixed and stores the resulting hex.

const EPSILON = 216 / 24389;
const KAPPA = 24389 / 27;

// D50 reference white (CSS Color 4).
const D50 = [0.9642956764295677, 1, 0.8251046025104602];

type Matrix = [number[], number[], number[]];

const D50_TO_D65: Matrix = [
  [0.9554734527042182, -0.023098536874261423, 0.0632593086610217],
  [-0.028369706963208136, 1.0099954580058226, 0.021041398966943008],
  [0.012314001688319899, -0.020507696433477912, 1.3303659366080753],
];

const D65_TO_D50: Matrix = [
  [1.0479298208405488, 0.022946793341019088, -0.05019222954313557],
  [0.029627815688159344, 0.990434484573249, -0.01707382502938514],
  [-0.009243058152591178, 0.015055144896577895, 0.7518742899580008],
];

const XYZ_D65_TO_LINEAR_SRGB: Matrix = [
  [3.2409699419045226, -1.537383177570094, -0.4986107602930034],
  [-0.9692436362808796, 1.8759675015077202, 0.04155505740717559],
  [0.05563007969699366, -0.20397695888897652, 1.0569715142428786],
];

const LINEAR_SRGB_TO_XYZ_D65: Matrix = [
  [0.41239079926595934, 0.357584339383878, 0.1804807884018343],
  [0.21263900587151027, 0.715168678767756, 0.07219231536073371],
  [0.01933081871559182, 0.11919477979462598, 0.9505321522496607],
];

function multiply(matrix: Matrix, [x, y, z]: number[]): number[] {
  return matrix.map((row) => row[0]! * x! + row[1]! * y! + row[2]! * z!);
}

function gammaEncode(channel: number): number {
  return channel <= 0.0031308 ? 12.92 * channel : 1.055 * Math.pow(channel, 1 / 2.4) - 0.055;
}

function gammaDecode(channel: number): number {
  return channel <= 0.04045 ? channel / 12.92 : Math.pow((channel + 0.055) / 1.055, 2.4);
}

export function lchToHex(l: number, c: number, h: number): string {
  const hr = (h * Math.PI) / 180;
  const a = c * Math.cos(hr);
  const b = c * Math.sin(hr);

  const fy = (l + 16) / 116;
  const fx = fy + a / 500;
  const fz = fy - b / 200;
  const xr = fx ** 3 > EPSILON ? fx ** 3 : (116 * fx - 16) / KAPPA;
  const yr = l > KAPPA * EPSILON ? fy ** 3 : l / KAPPA;
  const zr = fz ** 3 > EPSILON ? fz ** 3 : (116 * fz - 16) / KAPPA;

  const xyzD50 = [xr * D50[0]!, yr * D50[1]!, zr * D50[2]!];
  const linear = multiply(XYZ_D65_TO_LINEAR_SRGB, multiply(D50_TO_D65, xyzD50));

  return `#${linear
    .map((channel) => {
      const clamped = Math.min(1, Math.max(0, gammaEncode(channel)));
      return Math.round(clamped * 255)
        .toString(16)
        .padStart(2, "0");
    })
    .join("")}`;
}

// {l, c, h} with h normalized to [0, 360). Hue is meaningless at zero chroma
// (grays) but stays a number, so callers can use it unconditionally.
export function hexToLch(hex: string): { l: number; c: number; h: number } {
  const linear = [1, 3, 5].map((offset) =>
    gammaDecode(parseInt(hex.slice(offset, offset + 2), 16) / 255),
  );
  const xyzD50 = multiply(D65_TO_D50, multiply(LINEAR_SRGB_TO_XYZ_D65, linear));

  const f = (t: number) => (t > EPSILON ? Math.cbrt(t) : (KAPPA * t + 16) / 116);
  const fx = f(xyzD50[0]! / D50[0]!);
  const fy = f(xyzD50[1]! / D50[1]!);
  const fz = f(xyzD50[2]! / D50[2]!);

  const l = 116 * fy - 16;
  const a = 500 * (fx - fy);
  const b = 200 * (fy - fz);
  const c = Math.hypot(a, b);
  const h = ((Math.atan2(b, a) * 180) / Math.PI + 360) % 360;

  return { l, c, h };
}
